/**
 * Submits a form programmatically while preserving the clicked submit button's
 * name/value pair (e.g. FormButton=Paypal). form.submit() bypasses the 'submit'
 * event entirely and therefore never includes the button that triggered it -
 * unlike a native click-triggered submission. Rather than re-dispatching a
 * 'submit' event via requestSubmit() (which is a no-op when called from within
 * the very submit handler that's already firing for this form, per the HTML
 * spec's reentrancy guard), mirror the submitter's name/value into a hidden
 * field before calling submit() - the same pattern already used for the
 * Google Pay flow's #MobileWalletButton field.
 *
 * @param {HTMLFormElement} form - The form element to submit.
 * @param {HTMLElement} [submitter] - The submit button that triggered the submission.
 * @returns {void}
 */
export const submitFormWithSubmitterValue = (form, submitter) => {
    if (submitter && submitter.name) {
        let hidden = form.querySelector('input.js-submitter-value');
        if (!hidden) {
            hidden = document.createElement('input');
            hidden.type = 'hidden';
            hidden.classList.add('js-submitter-value');
            form.appendChild(hidden);
        }
        hidden.name = submitter.name;
        hidden.value = submitter.value;
    }
    form.submit();
}

/**
 * Reveals hidden address input fields when user chooses to enter address manually.
 * Updates ARIA attributes for accessibility and optionally moves focus to the first visible field.
 * 
 * @param {Event} event - The click event from the "enter manually" button
 * @param {HTMLElement} el - The button element that triggers the reveal action
 * @param {boolean} [focus=true] - Whether to automatically focus the first visible field after revealing
 * @returns {void}
 * 
 * @example
 * // Reveal fields and focus first input
 * revealHiddenAddressFields(button);
 * 
 * @example
 * // Reveal fields without focusing (e.g., during form submission)
 * revealHiddenAddressFields(button, false);
 */
export function revealHiddenAddressFields(e, el, focus=true) {
    e.preventDefault();
	const $button = $(el);
	const $buttonContainer = $button.parent();
	const $addressContainer = $buttonContainer.siblings('.edit-fields');

	// Show the address fields
	$addressContainer.show();

	// Update ARIA state to announce the change to screen readers
	$button.attr('aria-expanded', 'true');
	$button.hide();

	// Move focus to the first input field in the revealed section
	const $firstField = $addressContainer.find('input, select, textarea').filter(':visible').first();
	if ($firstField.length > 0 && focus) {
		setTimeout(() => $firstField.trigger('focus'), 100);
	}
}

/**
 * Validates a form and handles focus management for validation errors.
 * If validation passes, submits the form. If validation fails, focuses the first
 * invalid field, handling both visible and hidden fields with appropriate ARIA support.
 * 
 * @param {HTMLFormElement} form - The form element to validate and submit
 * @returns {void}
 * 
 * @description
 * For visible invalid fields, focuses the field directly.
 * For hidden invalid fields, focuses the associated label with temporary tabindex
 * to ensure keyboard users can navigate to the error.
 * 
 * @example
 * handleManualValidateandFocus(document.querySelector('#UserDetails'));
 */
export const handleManualValidateandFocus = (form, submitter) => {
    if ($(form).valid()) {
        submitFormWithSubmitterValue(form, submitter);
    } else {
        const firstInvalidField = $($(form).validate().errorList[0].element);
        if (firstInvalidField.is(':visible')) {
            firstInvalidField[0].focus();
        } else {
            const firstInvalidLabel = firstInvalidField.closest('.errorbox').find('label').first();           
            if (firstInvalidLabel.length > 0) {
                $(firstInvalidLabel).attr('tabindex', '0');
                $(firstInvalidLabel).focus();
                $(firstInvalidLabel).on('blur', function () {
                    $(this).removeAttr('tabindex');
                });
            }
        }
    }
}

/**
 * Handles form submission for forms with hidden address fields.
 * Programmatically reveals any hidden address fields to ensure their values
 * are included in form validation and submission, then validates and submits the form.
 * 
 * @param {Event} e - The form submit event
 * @param {HTMLFormElement} form - The form element being submitted
 * @returns {void}
 * 
 * @description
 * This function prevents the default form submission, reveals any address fields
 * hidden behind "enter manually" buttons (without focusing them), and then
 * performs manual validation and submission. This ensures address data is not
 * lost when users interact with the Loqate address finder.
 * 
 * @example
 * $('form#UserDetails').on('submit', function(e) {
 *     handleHiddenAddressFields(e, this);
 * });
 */
export const handleHiddenAddressFields = (e, form) => {
    const submitter = e.originalEvent?.submitter;
    // Exit if back button is clicked
    if (submitter?.classList?.contains('back-button')) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    const enterManuallyButtons = document.querySelectorAll('.js-enter-address-manually');
    if (enterManuallyButtons.length > 0) {
        // trigger clcik to reveal fields hidden behind the button, so that their values can be included in the form submission
        enterManuallyButtons.forEach(button => revealHiddenAddressFields(e, button, false));
    }
    handleManualValidateandFocus(form, submitter);
}

/**
 * Associates an error message with a form field.
 * @param {HTMLElement} field - The form field to associate the error with
 * @param {HTMLElement} messageContainer - The element containing the error message
 */
export const associateErrorMessage = (field, messageContainer) => {
    if(!field || !messageContainer) {
        console.warn('associateErrorMessage: Missing field or message container. Cannot associate error message.', field, messageContainer);
        return;
    }
    // Implementation for associating error message with form field using ARIA attributes
    messageContainer.setAttribute('id', `${field.name}-error`);
    field.setAttribute('aria-describedby', messageContainer.id);
};